import { useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@/app/redux/store";
import {
  gxApprovalNonceApi,
  gxPaymentApproveApi,
  gxPaymentBulkApproveApi,
  gxPaymentPendingViewApi,
} from "@/app/apiConfig/services/paymentsAbstractApiLayer";
import { generatePkcePair, setCookie } from "@/app/services/Login/pkceService";
import { currentDomain, setConsentData } from "@/app/utils/helpers";
import { useAppRoutingType } from "@/app/hooks/useAppRoutingType";
import { Constants } from "@/app/utils/AppConstants";
import type {
  ApprovalRequiredTransactions,
  PaymentTransaction,
  SingleApproveResponse,
} from "@/app/types/payments";

export function usePaymentApprovalFlow(authzType: "TRANS" | "RTP") {
  const { appRoutingType } = useAppRoutingType();
  const user = useSelector((state: RootState) => state?.userData.data);

  const [transactionDetails, setTransactionDetails] =
    useState<PaymentTransaction>();
  const [transactionDetailsLoader, setTransactionDetailsLoader] =
    useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<ApprovalRequiredTransactions>();

  const [openApprovalDrawer, setOpenApprovalDrawer] = useState(false);
  const [openRejectAndNoteModal, setOpenRejectAndNoteModal] = useState(false);
  const [isReject, setIsReject] = useState(false);
  const [remark, setRemark] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<any>();
  const [bulkApproveCount, setBulkApproveCount] = useState<string>();
  const [transactionFailCode, setTransactionFailCode] = useState<string | null>(
    null,
  );
  const [uuId, setUuId] = useState<string>();

  const getTransactionDetailsApi = async (
    item: ApprovalRequiredTransactions,
  ) => {
    setTransactionDetailsLoader(true);
    try {
      const payload = { transId: item?.transId, gxChnNo: item?.gxChnNo };
      const result = await gxPaymentPendingViewApi(payload);
      setTransactionDetails(
        result?.data !== null && result?.status === 1 ? result.data : undefined,
      );
    } catch (err) {
      console.log("Error in getTransactionDetailsApi::", err);
      setTransactionDetails(undefined);
    } finally {
      setTransactionDetailsLoader(false);
    }
  };

  const selectTransaction = async (item: ApprovalRequiredTransactions) => {
    setSelectedTransaction(item);
    await getTransactionDetailsApi(item);

    if (
      user?.lfiHolder === Constants.y &&
      (item?.paymentStatusCode === Constants.pendingConsentAuthorization ||
        item?.paymentStatusCode === Constants.awaitingApproval)
    ) {
      try {
        const result = await gxApprovalNonceApi();
        if (result.status === 1) {
          setUuId(result?.data?.nonceId);
        }
      } catch (err: any) {
        console.log("Error in nonceApi::", err);
        setTransactionFailCode(err?.errorCode);
        return;
      }
    }
    setOpenApprovalDrawer(true);
  };

  const handleSuccessResponse = (data: SingleApproveResponse) => {
    if (openRejectAndNoteModal) closeRemark();
    setSuccessData(
      data?.aprdStatus ? data : { ...data, aprdStatus: "consent" },
    );
    setOpenSuccessModal(true);
  };

  const approvePayment = async (aprdStatus: string) => {
    if (!transactionDetails?.transId) return;
    setIsLoading(true);

    const remarksPayload =
      aprdStatus === Constants.sentForRepair || remark
        ? { remarks: remark }
        : {};

    try {
      const { verifier, challenge } = await generatePkcePair();
      const payload = {
        transId: transactionDetails.transId,
        smeCompId: transactionDetails.smeCompanyid,
        gxChnNo: transactionDetails.gxChnNo,
        aprdStatus,
        authzType,
        ...remarksPayload,
        nonceId: uuId || "",
        codeChallenge:
          appRoutingType === Constants.centralBank ? challenge : null,
        appUrl:
          appRoutingType === Constants.centralBank
            ? `${currentDomain()}/dashboard`
            : null,
      };

      const result = await gxPaymentApproveApi(payload);
      if (result?.status === 1) {
        if (appRoutingType === Constants.centralBank) {
          setConsentData({
            flag: "A",
            open: false,
            openRtp: false,
            openSfr: false,
            successData: {
              noOfApprovalrequired: selectedTransaction
                ?.noOfApprovalrequired as number,
              aprdLevel: selectedTransaction?.aprdLevel as number,
              debitAmount: transactionDetails?.debitAmount as number,
              gxChnNo: transactionDetails?.gxChnNo as string,
              debitCurrency: transactionDetails?.debitCurrency as string,
              paymentInitiatedDate: `${new Date()}`,
              beneAccountName: selectedTransaction?.beneAccountName as string,
            },
          });
          setCookie(verifier);
        }
        handleSuccessResponse(result?.data);
      }
    } catch (err: any) {
      console.log("Error in approvePayment::", err);
      setTransactionFailCode(err?.errorCode);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = () => approvePayment(Constants.approve);

  const handleReject = () => {
    setOpenRejectAndNoteModal(true);
    setIsReject(true);
  };

  const submitReject = () => {
    approvePayment(isReject ? Constants.reject : Constants.sentForRepair);
  };

  const bulkApproveAndReject = async (
    transIds: number[],
    aprdStatus: string,
  ) => {
    if (transIds.length === 0) return;
    setIsLoading(true);
    try {
      const payload = { transId: transIds, remarks: "", aprdStatus, authzType };
      const result = await gxPaymentBulkApproveApi(payload);
      if (result?.data !== null) {
        setBulkApproveCount(
          `${result?.data?.successCount}/${result?.data?.totalCount}`,
        );
        setOpenSuccessModal(true);
      }
    } catch (err) {
      console.log("Error in bulkApproveAndReject::", err);
    } finally {
      setIsLoading(false);
    }
  };

  const closeRemark = () => {
    setOpenRejectAndNoteModal(false);
    setIsReject(false);
    setRemark("");
  };

  const closeSuccessModal = () => {
    setOpenApprovalDrawer(false);
    setOpenSuccessModal(false);
    setSuccessData(undefined);
    setBulkApproveCount(undefined);
    setTransactionFailCode(null);
  };

  return {
    // detail drawer
    transactionDetails,
    transactionDetailsLoader,
    selectedTransaction,
    openApprovalDrawer,
    setOpenApprovalDrawer,
    selectTransaction,

    // reject modal
    openRejectAndNoteModal,
    isReject,
    remark,
    setRemark,
    handleReject,
    submitReject,
    closeRemark,

    // approve
    handleApprove,
    bulkApproveAndReject,
    
    // status
    isLoading,
    openSuccessModal,
    successData,
    bulkApproveCount,
    transactionFailCode,
    closeSuccessModal,
  };
}
